import jsPDF from "jspdf";
import { loadLogoForPdf } from "./logoLoader";

/**
 * Convocazione partita — A4 portrait, mm units.
 * Layout: logo a sinistra (22×22mm), titolo centrato, sezione info + lista
 * giocatori. Prevenzione overlap: tutti gli elementi rispettano una griglia
 * con margine sinistro di 35mm quando il logo è presente (15→37), o 20mm
 * altrimenti.
 */
export async function exportConvocationPdf(convocation, players, team) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const callPlayers = players.filter((p) => convocation.player_ids.includes(p.id));

  const logo = await loadLogoForPdf(team);
  const hasLogo = !!logo;
  // Reserve a band on the left for the logo (15mm margin + 22mm logo + 4mm gap)
  const titleMarginTop = 12;

  // Header band background (subtle)
  doc.setFillColor(248, 250, 252);
  doc.rect(0, 0, pageW, 40, "F");
  doc.setDrawColor(15, 118, 110);
  doc.setLineWidth(0.6);
  doc.line(0, 40, pageW, 40);

  if (hasLogo) {
    try {
      doc.addImage(logo.dataUrl, logo.format, 15, 10, 22, 22);
    } catch (err) {
      console.warn("PDF logo embed failed:", err);
    }
  }

  // Title — centered using full page width
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(15, 118, 110);
  doc.text("CONVOCAZIONE PARTITA", pageW / 2, titleMarginTop + 6, { align: "center" });
  doc.setFontSize(11);
  doc.setTextColor(60);
  doc.setFont("helvetica", "normal");
  doc.text(`${team.name} · ${team.category || ""}`, pageW / 2, titleMarginTop + 13, { align: "center" });
  if (team.season) {
    doc.setFontSize(9);
    doc.text(`Stagione ${team.season}`, pageW / 2, titleMarginTop + 19, { align: "center" });
  }
  doc.setTextColor(20);

  let y = 50;

  // Match title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(convocation.title || "Partita", 20, y);
  y += 7;

  // Info block in two columns
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const colL = 20;
  const colR = 110;
  const info = [];
  if (convocation.opponent) info.push(["Avversario", convocation.opponent]);
  if (convocation.match_date) info.push(["Data partita", convocation.match_date]);
  if (convocation.meeting_time) info.push(["Ritrovo", `${convocation.meeting_time}${convocation.meeting_place ? ` — ${convocation.meeting_place}` : ""}`]);
  if (convocation.location) info.push(["Campo", convocation.location]);

  info.forEach(([label, value], i) => {
    const col = i % 2 === 0 ? colL : colR;
    if (i % 2 === 0 && i > 0) y += 7;
    doc.setFontSize(8);
    doc.setTextColor(110);
    doc.text(label.toUpperCase(), col, y);
    doc.setFontSize(10);
    doc.setTextColor(20);
    doc.setFont("helvetica", "bold");
    doc.text(String(value), col, y + 4.5);
    doc.setFont("helvetica", "normal");
  });
  y += 12;

  // Separator
  doc.setDrawColor(220);
  doc.setLineWidth(0.3);
  doc.line(20, y, pageW - 20, y);
  y += 8;

  // Convocati title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(15, 118, 110);
  doc.text(`CONVOCATI (${callPlayers.length})`, 20, y);
  doc.setTextColor(20);
  y += 6;

  // Two-column player list to save space
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const sorted = callPlayers.slice().sort((a, b) => (a.jersey_number || 99) - (b.jersey_number || 99));
  const half = Math.ceil(sorted.length / 2);
  const colWidth = (pageW - 40) / 2 - 4;
  sorted.forEach((p, i) => {
    const col = i < half ? 20 : 20 + colWidth + 8;
    const row = i < half ? i : i - half;
    const yy = y + row * 6.5;
    if (yy > 275) { /* will paginate later */ }
    const num = p.jersey_number != null ? `#${String(p.jersey_number).padStart(2, "0")}` : "—";
    const role = p.role ? `  (${p.role})` : "";
    doc.text(`${row + 1}. ${num}  ${p.last_name} ${p.first_name}${role}`, col, yy);
  });
  y += half * 6.5 + 10;

  // Notes
  if (convocation.notes) {
    if (y > 250) { doc.addPage(); y = 20; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(15, 118, 110);
    doc.text("NOTE", 20, y);
    doc.setTextColor(20);
    doc.setFont("helvetica", "normal");
    y += 5;
    const lines = doc.splitTextToSize(convocation.notes, pageW - 40);
    doc.text(lines, 20, y);
    y += lines.length * 5 + 5;
  }

  // Footer
  const pageH = doc.internal.pageSize.getHeight();
  doc.setFontSize(7);
  doc.setTextColor(140);
  doc.text(
    `Generato il ${new Date().toLocaleString("it-IT")} · ${team.name || ""}`,
    pageW / 2, pageH - 10, { align: "center" }
  );

  doc.save(`convocazione-${(convocation.title || "partita").replace(/\s+/g, "-")}.pdf`);
}
